import {
  NEVER,
  catchError,
  defer,
  from,
  switchMap,
  throwError,
  type MonoTypeOperatorFunction,
  type Observable,
} from "rxjs";
import { getConsoleAccessToken } from "@/lib/auth/consoleAccessToken";
import {
  endConsoleSessionAndLogOut,
  handleConsoleUnauthorized,
  UNAUTHORIZED_OUTCOME,
} from "@/lib/auth/consoleSessionLost";
import { extractHttpStatusFromUnknown } from "@/lib/http/extractHttpStatusFromUnknown";
import { getPublicRuntimeConfig } from "@/store/app/publicRuntimeConfigInstance";

const isUnauthorized = (error: unknown): boolean =>
  extractHttpStatusFromUnknown(error) === 401;

/**
 * Add as the last operator of every console API request pipeline. The generated
 * client has no error hook (its `post` middleware only sees successes), so 401
 * handling lives here: renew (or detect an already renewed token) and re-subscribe
 * once; end the session when Cognito rejects the renew, and log out of Cognito
 * when the fresh token is rejected too. While the user is leaving for Cognito the
 * request never emits, so no error state flashes.
 *
 * The source must build a fresh request on every subscription (the
 * `from(getXApi()).pipe(mergeMap((api) => api.method()))` shape does), so the retry
 * is sent with the renewed token.
 */
export const retryOnUnauthorized =
  <T>(): MonoTypeOperatorFunction<T> =>
  (source: Observable<T>): Observable<T> =>
    defer(() => {
      const sentToken = getConsoleAccessToken();
      return source.pipe(
        catchError((error: unknown) => {
          if (!isUnauthorized(error)) {
            return throwError(() => error);
          }
          const config = getPublicRuntimeConfig();
          return from(handleConsoleUnauthorized(config, sentToken)).pipe(
            switchMap((outcome) => {
              if (outcome === UNAUTHORIZED_OUTCOME.LOST) {
                return NEVER;
              }
              if (outcome === UNAUTHORIZED_OUTCOME.UNAVAILABLE) {
                return throwError(() => error);
              }
              return source.pipe(
                catchError((retryError: unknown) => {
                  if (!isUnauthorized(retryError)) {
                    return throwError(() => retryError);
                  }
                  return from(endConsoleSessionAndLogOut(config)).pipe(
                    switchMap((redirecting) =>
                      redirecting ? NEVER : throwError(() => error),
                    ),
                  );
                }),
              );
            }),
          );
        }),
      );
    });
